import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Stack, useMediaQuery, useTheme } from '@mui/material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import { useEntityPage } from 'c-hooks';
import { CampaignOverviewDisplays } from 'c-main/Components';
import type { Campaign } from 'c-sdk';
import { PermissionName, UserType } from 'c-sdk';
import { Download, Menu as MenuIcon } from '@mui/icons-material';
import Sidebar from 'c-main/Pages/Campaign/CampaignOverviewPage/SideBar/Sidebar';
import ReactGA from 'react-ga';
import type { SystemSearchEntityResource } from 'c-system-search';
import { isObjectEmpty } from 'c-main/Pages/Campaign/campaignUtils';
import { IfHasAllPermissions, IfUserIsOneOfTypes } from 'c-auth-module/Components';
import { Button, LinkButton } from 'c-components';
import apiClient from 'c-data/apiClient';
import { useAtom } from 'jotai/index';
import { atom_campaignUploadArtworkOpen } from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import useCampaignCreativesData from 'c-main/Components/CreativeManagement/useCampaignCreativesData';
import CampaignInstructions from 'c-main/Pages/Campaign/CampaignOverviewPage/CampaignInstructionsDialog';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import UploadDialog from 'c-main/Components/CreativeManagement/Components/Creatives/UploadDialog';
import AiCampaignChat from 'c-main/Pages/Campaign/AiCampaignChat/AiCampaignChat';
import { campaignIcon } from 'c-main/icons';

const CreateCreativesPerms: PermissionName[] = [PermissionName.UniledportalCreativeCreate];
const SpecsDownloadPermissions = [PermissionName.UniledportalCampaignSpecsDownload];
const CreativeSpecsDownloadPermissions = [PermissionName.UniledportalCampaignCreativeDownload];
const CreativeSpecsDownloadUserTypes = [UserType.Admin, UserType.Agency, UserType.Buyer];

const CampaignDisplaysPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const { id } = useEntityPage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const toggleMobileDrawer = useCallback(() => {
        setMobileDrawerOpen(prev => !prev);
    }, []);

    const t = useCommonTranslation();

    return (
        <>
            {isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'grey.300',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: 'background.paper',
                        }}
                    >
                        <IconButton onClick={toggleMobileDrawer} edge="start" aria-label="menu">
                            <MenuIcon />
                        </IconButton>
                    </Box>
                    <Sidebar
                        id={id}
                        mobileDrawerOpen={mobileDrawerOpen}
                        toggleMobileDrawer={toggleMobileDrawer}
                    />
                    <Box sx={{ flexGrow: 1, px: 2, pt: 2 }}>
                        <CampaignOverviewDisplays id={id} />
                    </Box>
                </Box>
            )}
            {!isMobile && (
                <Box sx={{ display: 'flex' }}>
                    <Sidebar id={id} />
                    <Box sx={{ flexGrow: 1, pt: 2, px: 2 }}>
                        <CampaignOverviewDisplays id={id} />
                    </Box>
                </Box>
            )}
        </>
    );
};

const PageBreadcrumbs: SystemSearchEntityResource<Campaign>['renderEntityBreadcrumbs'] = ({
    entity,
}) => {
    const noPcaData = isObjectEmpty(entity?.pca_available);
    const t = useCommonTranslation();
    const specDownloadBtn = useMemo(
        () => (
            <>
                <IfUserIsOneOfTypes roles={CreativeSpecsDownloadUserTypes}>
                    <IfHasAllPermissions permissions={SpecsDownloadPermissions}>
                        <LinkButton
                            id="campaign-header-download-spec"
                            variant="outlined"
                            sx={{ ml: 'auto' }}
                            to={apiClient.Entities.Campaign.downloadLink(entity.id, {
                                include_display_specs: true,
                            })}
                            target="_blank"
                        >
                            <Download fontSize="inherit" />{' '}
                            {t('Modules.Main.Campaigns.Overview.ActionLabels.downloadSpec')}
                        </LinkButton>
                    </IfHasAllPermissions>
                </IfUserIsOneOfTypes>
            </>
        ),
        [entity.id, t],
    );

    const creativesDownloadBtn = useMemo(
        () => (
            <>
                <IfHasAllPermissions permissions={CreativeSpecsDownloadPermissions}>
                    <LinkButton
                        id="campaign-header-download-creatives"
                        variant="outlined"
                        sx={{ ml: 'auto' }}
                        to={apiClient.Entities.Campaign.downloadLink(entity.id, {
                            include_creatives: true,
                        })}
                        target="_blank"
                    >
                        <Download fontSize="inherit" />{' '}
                        {t('Modules.Main.Campaigns.Overview.ActionLabels.downloadCreatives')}
                    </LinkButton>
                </IfHasAllPermissions>
            </>
        ),
        [entity.id, t],
    );
    const [artworkDialogOpen, setArtworkDialogOpen] = useAtom(atom_campaignUploadArtworkOpen);

    const openUploadDialog = useCallback(() => {
        setArtworkDialogOpen(true);
    }, [setArtworkDialogOpen]);
    const closeUploadDialog = useCallback(() => {
        setArtworkDialogOpen(false);
    }, [setArtworkDialogOpen]);
    const { updateCreatives, reValidateCampaign, existingFilesArray } = useCampaignCreativesData(
        entity.id,
    );

    const uploadArtworkBtn = useMemo(
        () => (
            <IfHasAllPermissions permissions={CreateCreativesPerms}>
                <Button onClick={openUploadDialog} id="campaign-header-upload-artwork">
                    {t('Modules.Main.Campaigns.Overview.ActionLabels.uploadArtwork')}
                </Button>
            </IfHasAllPermissions>
        ),
        [t, openUploadDialog],
    );

    return (
        <Box display="flex">
            <Box ml="auto">
                <Stack gap={1} direction="row" id="campaign-header-buttons">
                    {uploadArtworkBtn}
                    {specDownloadBtn}
                    {creativesDownloadBtn}
                    <CampaignInstructions campaignId={entity.id} />

                    <DndProvider backend={HTML5Backend}>
                        <UploadDialog
                            isDialogOpen={artworkDialogOpen}
                            closeDialog={closeUploadDialog}
                            updateCreatives={updateCreatives}
                            reValidateCampaign={reValidateCampaign}
                            existingFilesArray={existingFilesArray}
                        />
                    </DndProvider>
                    {noPcaData === false && <AiCampaignChat campaignId={entity.id} />}
                </Stack>
            </Box>
        </Box>
    );
};

const setup: PageEntityConfig<Campaign> = {
    id: 'CampaignDisplaysPage',
    component: CampaignDisplaysPage,
    systemSearch: {
        title: undefined,
        description: 'Modules.Main.Campaigns.Overview.description',
        icon: campaignIcon,
        type: 'entity',
        aliases: undefined,
        route: PostAuthRoutes.Campaigns.CampaignDisplays,
        saveAsRecentPage: true,
        prefix: 'SystemSearch.prefixes.campaign',
        prefixDescription: 'SystemSearch.prefixDescriptions.campaign',
        breadcrumbs: [
            {
                label: 'Modules.Main.Campaigns.List.metaTitle',
                link: PostAuthRoutes.Campaigns.CampaignList,
            },
        ],
        entityName: 'Campaign',
        nameGen: entity => `${entity?.name}-${entity?.id}`,
        permissions: [PermissionName.UniledportalCampaignRead],
        renderEntityBreadcrumbs: PageBreadcrumbs,
        defaultIncludes: [
            'agency',
            'market',
            'creatives',
            'client',
            'creatives.resolution',
            'creatives.file',
            'budgetSummary',
            'budgetSummary.lineItems',
            'budgetSummaries',
            'budgetSummaries.lineItems',
            'notices',
            'errors',
            'creativeThumbnails',
            'bookingSummaries',
            'popShotsCount',
            'missingDisplaysCount',
            'happydemics',
            'additionalContacts',
            // 'budgetSummaries.lineItems.lineItem',
        ],
    },
};

export default setup;
